/**
 * Server-Sent Events (SSE) client for Dafne real-time notifications.
 *
 * Features:
 * - Automatic reconnection with exponential backoff
 * - Per-tab session ID for independent topic subscriptions
 * - Topic subscription management with batching and retry
 * - Anonymous/authenticated mode switching
 *
 * Usage:
 * 1. Initialize in app bootstrap: SSEClient.initialize({ ... })
 * 2. Connect: SSEClient.instance.connectAppropriate()
 * 3. Subscribe to topics: SSEClient.instance.subscribeTopic('user:123')
 * 4. Listen for events: SSEClient.instance.addEventListener('', handler)
 */

import { getApiBaseUrl } from "../api-config";

export type EventSourceHandler = (data: unknown, eventName: string) => void;

export interface SSEClientOptions {
  /** Callback when connection state changes */
  onlineCallback: (isOnline: boolean) => void;
  /** Callback after successfully connecting */
  afterOnlineEvent?: () => void | Promise<void>;
  /** Base URL for API (defaults to current origin) */
  baseUrl?: string;
  /** Function to get auth token for API calls */
  getAuthToken?: () => string | null;
  /** Function to check if user is logged in */
  isLoggedIn?: () => boolean;
}

interface TopicBatchResult {
  added?: Record<string, boolean>;
  removed?: Record<string, boolean>;
}

const ALL_EVENTS = "__all__";
const SESSION_ID_KEY = "dafne-sse-session-id";

/**
 * Generates a random UUID v4.
 */
function newGuid(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export class SSEClient {
  private static _instance: SSEClient | undefined;

  /** Get the singleton instance (throws if not initialized) */
  static get instance(): SSEClient {
    if (!this._instance) {
      throw new Error(
        "SSEClient not initialized. Call SSEClient.initialize() in app bootstrap.",
      );
    }
    return this._instance;
  }

  /** Initialize the singleton instance */
  static initialize(options: SSEClientOptions): SSEClient {
    if (this._instance) {
      console.warn("SSEClient already initialized");
      return this._instance;
    }
    this._instance = new SSEClient(options);
    return this._instance;
  }

  /** Check if initialized */
  static get isInitialized(): boolean {
    return !!this._instance;
  }

  private readonly _options: SSEClientOptions;
  private readonly _sessionId: string;
  private _source?: EventSource;
  private _mode: "anonymous" | "user" = "anonymous";
  private _events: Record<string, EventSourceHandler[]> = {};

  // Reconnection state
  private _reconnectAttempt = 0;
  private _maxReconnectDelay = 64000; // 64 seconds max
  private _baseReconnectDelay = 1000;
  private _reconnectTimer?: ReturnType<typeof setTimeout>;
  private _nextReconnectAt?: number;

  // Topic management
  private _topicRefCounts = new Map<string, number>();
  private _serverTopics = new Set<string>();
  private _pendingBatch = new Map<string, "add" | "remove">();
  private _batchTimer?: ReturnType<typeof setTimeout>;
  private _batchDelayMs = 10;
  private _batchRetryAttempt = 0;
  private _isFlushingBatch = false;

  private constructor(options: SSEClientOptions) {
    this._options = options;

    // Restore session ID from storage (survives page refresh)
    const stored = sessionStorage.getItem(SESSION_ID_KEY);
    if (stored) {
      this._sessionId = stored;
    } else {
      this._sessionId = newGuid();
      sessionStorage.setItem(SESSION_ID_KEY, this._sessionId);
    }
  }

  /** The stable per-tab session ID */
  get sessionId(): string {
    return this._sessionId;
  }

  /** When the next reconnect attempt is scheduled (epoch ms) */
  get nextReconnectAt(): number | undefined {
    return this._nextReconnectAt;
  }

  /** Whether the connection is currently open */
  get isConnected(): boolean {
    return this._source?.readyState === EventSource.OPEN;
  }

  /** Current connection mode */
  get mode(): "anonymous" | "user" {
    return this._mode;
  }

  // ============================================================
  // Connection Management
  // ============================================================

  /** Connect in appropriate mode based on auth state */
  connectAppropriate(): void {
    const isLoggedIn = this._options.isLoggedIn?.() ?? false;
    if (isLoggedIn) {
      this._connectUser();
    } else {
      this._connectAnonymous();
    }
  }

  /** Establish authenticated connection */
  private _connectUser(): void {
    // If switching from anonymous, clear topic state
    if (this._mode === "anonymous" && this._source) {
      this._clearTopicState();
    }

    this.disconnect();
    this._mode = "user";

    const baseUrl = this._options.baseUrl ?? getApiBaseUrl();
    const url = `${baseUrl}/api/notification/sse?sessionid=${this._sessionId}`;

    this._source = new EventSource(url, { withCredentials: true });
    this._wireHandlers();
  }

  /** Establish anonymous (heartbeat-only) connection */
  private _connectAnonymous(): void {
    if (this._source && this._mode === "anonymous") {
      return; // Already connected anonymously
    }

    this.disconnect();
    this._mode = "anonymous";

    const baseUrl = this._options.baseUrl ?? getApiBaseUrl();
    const url = `${baseUrl}/api/notification/sse?sessionid=${this._sessionId}`;

    this._source = new EventSource(url, { withCredentials: true });
    this._wireHandlers();
  }

  /** Disconnect and clean up */
  disconnect(): void {
    this._source?.close();
    this._source = undefined;

    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = undefined;
    }
    this._nextReconnectAt = undefined;
  }

  private _wireHandlers(): void {
    if (!this._source) return;

    this._source.onopen = () => {
      console.log("[SSE] Connected", { mode: this._mode });
      this._resetReconnectBackoff();

      if (this._reconnectTimer) {
        clearTimeout(this._reconnectTimer);
        this._reconnectTimer = undefined;
      }
      this._nextReconnectAt = undefined;

      this._options.onlineCallback(true);

      try {
        this._options.afterOnlineEvent?.();
      } finally {
        if (this._mode === "user") {
          // Re-subscribe topics on reconnect
          this._serverTopics.clear();
          this._resubscribeTopics();
        }
      }
    };

    this._source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        const eventName = data.eventName as string;

        // Dispatch to specific listeners
        this._events[eventName]?.forEach((h) => h(data, eventName));

        // Dispatch to all-events listeners
        this._events[ALL_EVENTS]?.forEach((h) => h(data, eventName));
      } catch {
        // Ignore non-JSON (keep-alive comments)
      }
    };

    this._source.onerror = (err) => {
      console.log("[SSE] Error", err, { mode: this._mode });
      this._options.onlineCallback(false);

      this._source?.close();
      this._source = undefined;

      this._scheduleReconnect();
    };
  }

  private _scheduleReconnect(): void {
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);

    // Exponential backoff with jitter
    const delay = Math.min(
      this._baseReconnectDelay * Math.pow(2, this._reconnectAttempt),
      this._maxReconnectDelay,
    );
    const jitter = delay * 0.3 * (Math.random() * 2 - 1);
    const actualDelay = Math.max(250, delay + jitter);

    this._nextReconnectAt = Date.now() + actualDelay;
    this._reconnectAttempt++;

    console.log("[SSE] Scheduling reconnect", {
      delay: actualDelay,
      attempt: this._reconnectAttempt,
    });

    this._reconnectTimer = setTimeout(() => {
      this._nextReconnectAt = undefined;
      this.connectAppropriate();
    }, actualDelay);
  }

  private _resetReconnectBackoff(): void {
    this._reconnectAttempt = 0;
  }

  // ============================================================
  // Topic Subscription Management
  // ============================================================

  /** Subscribe to a topic. Returns true on success. */
  async subscribeTopic(topic: string): Promise<boolean> {
    topic = topic.trim();
    if (!topic) return false;

    const current = this._topicRefCounts.get(topic) ?? 0;
    this._topicRefCounts.set(topic, current + 1);

    // Only register server-side on 0->1 transition
    if (current > 0) return true;

    // Anonymous mode: track locally only
    if (this._mode === "anonymous") return true;

    if (this.isConnected) {
      this._queueBatch(topic, "add");
    }
    return true;
  }

  /** Unsubscribe from a topic. Returns true on success. */
  async unsubscribeTopic(topic: string): Promise<boolean> {
    topic = topic.trim();
    if (!topic) return false;

    const current = this._topicRefCounts.get(topic);
    if (current === undefined) return true;

    const next = current - 1;
    if (next > 0) {
      this._topicRefCounts.set(topic, next);
      return true; // Still in use elsewhere
    }

    // Remove tracking
    this._topicRefCounts.delete(topic);

    if (this._mode === "anonymous") return true;

    if (!this._serverTopics.has(topic)) return true;

    if (this.isConnected) {
      this._queueBatch(topic, "remove");
    }
    return true;
  }

  /** Get current topic subscription state (for debugging) */
  getTopicSummary(): {
    localTopics: Array<{ topic: string; refCount: number }>;
    serverTopics: string[];
    pendingBatch: Array<{ topic: string; action: "add" | "remove" }>;
  } {
    const localTopics: Array<{ topic: string; refCount: number }> = [];
    for (const [topic, refCount] of this._topicRefCounts.entries()) {
      localTopics.push({ topic, refCount });
    }

    return {
      localTopics: localTopics.sort((a, b) => a.topic.localeCompare(b.topic)),
      serverTopics: Array.from(this._serverTopics).sort(),
      pendingBatch: Array.from(this._pendingBatch.entries()).map(
        ([topic, action]) => ({
          topic,
          action,
        }),
      ),
    };
  }

  private _clearTopicState(): void {
    this._serverTopics.clear();
    this._pendingBatch.clear();
    if (this._batchTimer) {
      clearTimeout(this._batchTimer);
      this._batchTimer = undefined;
    }
    this._topicRefCounts.clear();
  }

  private _resubscribeTopics(): void {
    if (!this.isConnected) return;

    for (const topic of this._topicRefCounts.keys()) {
      if (!this._serverTopics.has(topic)) {
        this._queueBatch(topic, "add");
      }
    }

    this._batchRetryAttempt = 0;
    this._flushBatch();
  }

  private _queueBatch(topic: string, op: "add" | "remove"): void {
    this._pendingBatch.set(topic, op);
    this._scheduleBatchFlush(this._batchDelayMs);
  }

  private async _flushBatch(): Promise<void> {
    if (this._isFlushingBatch) return;
    if (!this.isConnected) return;
    if (this._pendingBatch.size === 0) return;

    this._isFlushingBatch = true;

    const adds: string[] = [];
    const removes: string[] = [];

    for (const [topic, op] of this._pendingBatch.entries()) {
      if (op === "add") adds.push(topic);
      else removes.push(topic);
    }
    this._pendingBatch.clear();

    // Filter out cancellations
    const cancel = new Set(adds.filter((a) => removes.includes(a)));
    const finalAdds = adds.filter((a) => !cancel.has(a));
    const finalRemoves = removes.filter((r) => !cancel.has(r));

    if (finalAdds.length === 0 && finalRemoves.length === 0) {
      this._isFlushingBatch = false;
      return;
    }

    try {
      const baseUrl = this._options.baseUrl ?? getApiBaseUrl();
      const response = await fetch(`${baseUrl}/api/notification/topics`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(this._options.getAuthToken?.()
            ? { Authorization: `Bearer ${this._options.getAuthToken()}` }
            : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          sessionId: this._sessionId,
          add: finalAdds.length ? finalAdds : undefined,
          remove: finalRemoves.length ? finalRemoves : undefined,
        }),
      });

      if (!response.ok) {
        console.warn("[SSE] Topic batch failed", response.status);
        this._requeueFailed(finalAdds, finalRemoves);
        this._scheduleRetry();
        return;
      }

      const result: TopicBatchResult = await response.json();

      if (result.added) {
        for (const [topic, success] of Object.entries(result.added)) {
          if (success) this._serverTopics.add(topic);
        }
      }

      if (result.removed) {
        for (const [topic, success] of Object.entries(result.removed)) {
          if (success) this._serverTopics.delete(topic);
        }
      }

      this._batchRetryAttempt = 0;
    } catch (err) {
      console.warn("[SSE] Topic batch error", err);
      this._requeueFailed(finalAdds, finalRemoves);
      this._scheduleRetry();
    } finally {
      this._isFlushingBatch = false;
    }
  }

  private _requeueFailed(adds: string[], removes: string[]): void {
    for (const a of adds) this._pendingBatch.set(a, "add");
    for (const r of removes) this._pendingBatch.set(r, "remove");
  }

  private _scheduleRetry(): void {
    this._batchRetryAttempt++;
    const delay = Math.min(
      250 * Math.pow(2, this._batchRetryAttempt - 1),
      5000,
    );
    this._scheduleBatchFlush(delay);
  }

  private _scheduleBatchFlush(delay: number): void {
    if (this._batchTimer) clearTimeout(this._batchTimer);
    this._batchTimer = setTimeout(() => this._flushBatch(), delay);
  }

  // ============================================================
  // Event Listener Management
  // ============================================================

  /**
   * Add an event listener.
   * @param eventName Event name to listen for, or '' for all events
   * @param handler Handler function
   */
  addEventListener(
    eventName: string | string[],
    handler: EventSourceHandler,
  ): void {
    const names = Array.isArray(eventName) ? eventName : [eventName];

    for (const name of names) {
      const key = name === "" ? ALL_EVENTS : name;
      if (!this._events[key]) {
        this._events[key] = [];
      }
      if (!this._events[key].includes(handler)) {
        this._events[key].push(handler);
      }
    }
  }

  /**
   * Remove an event listener.
   * @param eventName Event name, or '' for all events
   * @param handler Handler function to remove
   */
  removeEventListener(
    eventName: string | string[],
    handler: EventSourceHandler,
  ): void {
    const names = Array.isArray(eventName) ? eventName : [eventName];

    for (const name of names) {
      const key = name === "" ? ALL_EVENTS : name;
      if (this._events[key]) {
        this._events[key] = this._events[key].filter((h) => h !== handler);
      }
    }
  }

  /**
   * Push a synthetic event (for testing or local events).
   */
  pushEvent(eventName: string, data: unknown): void {
    this._events[eventName]?.forEach((h) => h(data, eventName));
    this._events[ALL_EVENTS]?.forEach((h) => h(data, eventName));
  }
}
